<template>
  <BaseEditModal
    :masterObj="{}"
    displayName="Create Asset"
    helpFileName="NewAsset"
    :tabs="tabs"
    :saveFunc="create_custom_asset"
    :deleteFunc="() => {}"
    :instanceDeletable="false"
    icon="mdi-image-plus"
    hideBottomButtons=""
    @close="$emit('close')"
    @activeTab="updateActiveTab">
    <template v-slot:append-buttons>
      <v-btn
        @click="create_custom_asset()"
        :loading="saveLoading"
        color="primary"
        block
        variant="flat">
        Create
      </v-btn>
    </template>
  </BaseEditModal>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { $http } from '../../stores/http.js';
import { state } from '../../stores/deluge.js';
import BaseEditModal from '../Shared/Modals/BaseEditModal.vue';
import CreateAssetDropbox from './CreateAssetModalComponents/CreateAssetDropbox.vue';
import CreateAssetLiveStream from './CreateAssetModalComponents/CreateAssetLiveStream.vue';
import CreateAssetVideoFeed from './CreateAssetModalComponents/CreateAssetVideoFeed.vue';
import CreateAssetNdi from './CreateAssetModalComponents/CreateAssetNDI.vue';
import CreateAssetText from './CreateAssetModalComponents/CreateAssetText.vue';
import CreateAssetJson from './CreateAssetModalComponents/CreateAssetJSON.vue';
const emit = defineEmits(['close']);
const saveLoading = ref(false);
const activeTabIndex = ref(0);

// Tab data objects
const dropboxData = reactive({
  url: '',
  name: '',
  conflict: 'rename',
});

const liveStreamData = reactive({
  name: '',
  url: '',
  username: '',
  password: '',
  auth: false,
});

const videoFeedData = reactive({
  name: '',
  input: '',
});

const ndiData = reactive({
  name: '',
  ndi_name: '',
});

const textData = reactive({
  name: '',
  text: '',
});

const jsonData = reactive({
  name: '',
  json_text: {},
  live: false,
  url: '',
  interval: 60,
});

const tabs = [
  {
    id: 0,
    title: 'Dropbox',
    icon: 'mdi-dropbox',
    component: CreateAssetDropbox,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(dropboxData, data);
      },
    },
  },
  {
    id: 1,
    title: 'Live Stream',
    icon: 'mdi-video-wireless',
    component: CreateAssetLiveStream,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(liveStreamData, data);
      },
    },
  },
  {
    id: 2,
    title: 'Video Feed',
    icon: 'mdi-video-input-hdmi',
    component: CreateAssetVideoFeed,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(videoFeedData, data);
      },
    },
  },
  {
    id: 3,
    title: 'NDI',
    icon: 'mdi-access-point-network',
    component: CreateAssetNdi,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(ndiData, data);
      },
    },
  },
  {
    id: 4,
    title: 'Text',
    icon: 'mdi-text-box',
    component: CreateAssetText,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(textData, data);
      },
    },
  },
  {
    id: 5,
    title: 'JSON',
    icon: 'mdi-code-json',
    component: CreateAssetJson,
    display: true,
    props: {},
    emits: {
      'update:data': (data) => {
        Object.assign(jsonData, data);
      },
    },
  },
];

function updateActiveTab(newTabIndex) {
  activeTabIndex.value = newTabIndex;
}

async function create_custom_asset() {
  saveLoading.value = true;

  try {
    let data = {};

    switch (activeTabIndex.value) {
      case 0:
        const normalizedUrl = dropboxData.url?.trim();
        if (!normalizedUrl) {
          throw new Error('Dropbox URL is required');
        }
        const dropboxPayload = {
          url: normalizedUrl,
        };
        if (dropboxData.name) {
          dropboxPayload.filename = dropboxData.name.trim();
        }
        if (dropboxData.conflict) {
          dropboxPayload.conflict = dropboxData.conflict;
        }
        if (state.current_asset_group) {
          dropboxPayload.group_uid = state.current_asset_group;
        }

        // Fire-and-forget: close the modal immediately while the server
        // downloads from Dropbox in the background.  The asset will appear
        // once processing completes (WebSocket push).
        $http(
          'POST',
          '/upload/import_dropbox',
          JSON.stringify(dropboxPayload),
          300000,
          true
        ).catch((err) => console.error('Dropbox import failed:', err));
        emit('close');
        return;
      case 1:
        data = {
          name: liveStreamData.name,
          info: {},
          value: liveStreamData.url,
          asset_type: 'live_stream',
          location: '',
          ready: true,
        };
        // Add auth info if enabled
        if (liveStreamData.auth) {
          data.info.auth = true;
          data.info.username = liveStreamData.username;
          data.info.password = liveStreamData.password;
        }
        break;
      case 2:
        data = {
          name: videoFeedData.name,
          info: {},
          value: videoFeedData.input,
          asset_type: 'video_feed',
          location: '',
          ready: true,
        };
        break;
      case 3:
        data = {
          name: ndiData.name,
          info: {},
          value: ndiData.ndi_name,
          asset_type: 'ndi',
          location: 'NDI',
          ready: true,
        };
        break;
      case 4:
        data = {
          name: textData.name,
          info: {},
          value: textData.text,
          asset_type: 'text',
          location: '',
          ready: true,
        };
        break;
      case 5:
        data = {
          name: jsonData.name,
          info: {
            live: jsonData.live,
            interval: parseInt(jsonData.interval) || 60,
            url: jsonData.url,
          },
          value: jsonData.json_text,
          asset_type: 'json',
          location: '',
          ready: true,
        };
        break;
    }

    data.custom = true;
    console.log('Creating asset:', data, 'active tab:', activeTabIndex.value);
    await $http('POST', '/create_asset', JSON.stringify({ asset: data }));
    emit('close');
  } catch (error) {
    console.error('Error creating asset:', error);
    emit('close');
  } finally {
    saveLoading.value = false;
  }
}
</script>
